import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  OutlinedInputProps,
  Select
} from "@material-ui/core";
import { SelectProps } from "@material-ui/core/Select";
import { makeStyles } from "@saleor/macaw-ui";
import classNames from "classnames";
import React, { memo, useState } from "react";
import { FormattedMessage } from "react-intl";

import ChevronDownIcon from "../../../assets/images/vector-down.svg";
import MyTypography from "../MyTypography";
import { singleSelectFieldItemHeight } from "./consts";

export interface Choice<T = string, L = string | React.ReactNode> {
  value: T;
  label: L;
  disabled?: boolean;
  isSubHeader?: boolean;
}

export type Choices = Choice[];

export interface ISelectFieldProps {
  labelParser?: (value: any) => any;
  menuListCustomStyle?: React.CSSProperties;
  showPlaceHolderWhenSelect?: boolean;
  testId?: string;
  className?: string;
  disabled?: boolean;
  noScrollBar?: boolean;
  error?: boolean;
  hint?: string | React.ReactNode;
  label?: string | React.ReactNode;
  name?: string;
  selectProps?: SelectProps;
  placeholder?: string;
  InputProps?: OutlinedInputProps;
  required?: boolean;
  labelEmpty?: string | React.ReactNode;
  customSelectStyle?: any;
  id?: string;

  onChange(event: any, choice?: any);
  onBlur?(event: any);
}

interface SingleSelectFieldProps extends ISelectFieldProps {
  value?: string;
  initialValue?: string;
  choices: Choices;
  allowBlank?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "&	.MuiSelect-icon": {
        top: "calc(50% - 10px)"
      },
      "& .MuiSelect-root": {
        fontSize: (props: ISelectFieldProps) =>
          !props.customSelectStyle
            ? "1.4rem"
            : props.customSelectStyle.fontSize,
        color: `${theme.palette.grey[900]}`
      },
      "&.Mui-disabled": {
        opacity: ".6",
        "&:hover": {
          border: "1px solid #CCCCCC"
        },
        "& .MuiSelect-icon": {
          opacity: 0.5
        }
      }
    },
    formControl: {
      "& label": {
        top: "-3px"
      },
      width: "100%"
    },
    label: {
      zIndex: 3,
      color: theme.palette.grey[900]
    },
    noLabel: {
      padding: theme.spacing(2, 1.5)
    },
    paper: {
      maxHeight: `calc(${singleSelectFieldItemHeight}px * 10 + ${singleSelectFieldItemHeight}px * 0.5)`,
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)"
    },
    disabledMenuItem: {
      pointerEvents: "none"
    },
    muiMenuItem: {
      minHeight: (props: ISelectFieldProps) => (props.noScrollBar ? 32 : 36),
      height: (props: ISelectFieldProps) => (props.noScrollBar ? 32 : 36),
      color: theme.palette.grey[900]
    },
    subHeader: {
      color: theme.palette.grey[900],
      fontSize: "1.4rem",
      top: "auto"
    },
    placeholder: {
      color: theme.palette.grey[500],
      width: "92%",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden"
    }
  }),
  { name: "SingleSelectField" }
);

export const SingleSelectField: React.FC<SingleSelectFieldProps> = props => {
  const {
    className,
    disabled,
    error,
    label,
    choices,
    value,
    onChange,
    name,
    hint,
    showPlaceHolderWhenSelect,
    selectProps,
    placeholder = "Select an option...",
    InputProps,
    testId,
    labelEmpty,
    menuListCustomStyle,
    customSelectStyle,
    allowBlank,
    labelParser,
    id
  } = props;
  const classes = useStyles(props);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const choicesByKey: { [key: string]: string } =
    choices === undefined
      ? {}
      : choices.reduce((prev, curr) => {
          if (labelParser) {
            prev[curr.value] = labelParser?.(curr.label);
          } else {
            prev[curr.value] = curr.label;
          }
          return prev;
        }, {});
  const IconSelectCustom = props => (
    <img
      {...props}
      src={ChevronDownIcon}
      alt=""
      className={classNames(`material-icons ${props.className}`)}
    />
  );

  const Placeholder = ({ children }) => (
    <MyTypography variant="body3" className={classes.placeholder}>
      {children}
    </MyTypography>
  );

  const handleChange = event => {
    const value = event.target.value;
    const choice = choices.find(item => item.value === value);
    if (choice?.disabled) {
      event.preventDefault();
    } else {
      onChange(event, choice);
    }
  };

  return (
    <FormControl
      className={classNames(classes.formControl, className)}
      error={error}
      disabled={disabled}
    >
      <InputLabel className={classes.label} shrink={!!value}>
        {label}
      </InputLabel>
      <Select
        id={id}
        data-testid={testId}
        data-input-type="single-select-field"
        name={name}
        variant="outlined"
        fullWidth
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        displayEmpty={true}
        renderValue={(choiceValue: any) =>
          choiceValue?.length || allowBlank ? (
            choicesByKey[choiceValue.toString()]
          ) : (
            <Placeholder>{placeholder}</Placeholder>
          )
        }
        value={isOpen && showPlaceHolderWhenSelect ? "" : value || ""}
        onChange={handleChange}
        input={
          <OutlinedInput
            name={name}
            id={`input-${id}`}
            labelWidth={180}
            {...InputProps}
            onClick={() => null}
            classes={{
              ...(InputProps?.classes || {}),
              input: classNames(InputProps?.classes?.input, {
                [classes.noLabel]: !label
              })
            }}
          />
        }
        {...selectProps}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: `calc(${singleSelectFieldItemHeight}px * 10 + ${singleSelectFieldItemHeight}px * 0.5)`,
              borderRadius: "0 0 6px 6px",
              marginTop: "1rem",
              overflowY: "auto"
            },
            ...{ "data-testid": `menu-${testId}` }
          },
          MenuListProps: {
            className: "custom-select",
            style: menuListCustomStyle
          },
          getContentAnchorEl: null,
          autoFocus: false,
          anchorOrigin: { horizontal: "left", vertical: "bottom" },
          transformOrigin: { horizontal: "left", vertical: "top" }
        }}
        IconComponent={props => IconSelectCustom(props)}
        className={classes.root}
      >
        {choices.length > 0 ? (
          choices.map(choice => {
            if (choice.isSubHeader) {
              return (
                <ListSubheader className={classes.subHeader} key={choice.value}>
                  {choice.label}
                </ListSubheader>
              );
            }

            return (
              <MenuItem
                disabled={choice.disabled}
                onMouseEnter={event => {
                  if (customSelectStyle) {
                    event.currentTarget.style.color =
                      customSelectStyle?.textColor;
                  }
                }}
                onMouseLeave={event => {
                  if (customSelectStyle) {
                    event.currentTarget.style.color = "#222222";
                  }
                }}
                className={classNames(classes.muiMenuItem)}
                id={"select-field-option-" + choice.value}
                data-testid={"select-field-option-" + choice.value}
                value={choice.value}
                key={choice.value}
              >
                <Box className={customSelectStyle?.menuClass}>
                  {choice.label}
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem
            data-testid="select-field-option"
            data-test-disabled
            disabled={true}
          >
            {labelEmpty || (
              <FormattedMessage defaultMessage="No results found" />
            )}
          </MenuItem>
        )}
      </Select>
      {hint && <FormHelperText>{hint}</FormHelperText>}
    </FormControl>
  );
};
SingleSelectField.displayName = "SingleSelectField";
export default memo(SingleSelectField);
